<!--
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：ドラッグツリー
 *
 * 作成者：2021/11/22 ノア）黄
 * 最終修正者：2022/1/24 ノア）黄
 *
 -->
<template>
  <draggable
    class="dragArea"
    tag="ul"
    :list="data"
    :group="group"
    :move="checkMove"
    @change="onUpdate"
    v-bind="dragOptions"
  >
    <template v-if="isContextShow()">
      <div :class="{ 'last-node': isLast(el), 'root-node': el.level === '0' }" v-for="el in data" :key="el.id">
        <div class="node">
          <!-- 項目開閉 -->
          <span class="expander" v-if="el.children && el.children.length > 0" @click="() => onExpandIconClick(el)">
            <span v-if="el.expand">
              <tti-svg-icon icon-name="su_021"></tti-svg-icon>
            </span>
            <span v-else>
              <tti-svg-icon icon-name="su_022"></tti-svg-icon>
            </span>
          </span>
          <span v-else class="expandSkeleton"></span>
          <!-- 項目アイコン -->
          <span v-if="el.src" class="expandSkeleton">
            <tti-svg-icon :icon-name="el.src"></tti-svg-icon>
          </span>
          <span>{{ el.label }}</span>
        </div>
        <officeAllocDraggableTree
          v-if="el.children"
          :data="el.children"
          :type="type"
          :canDropToRoot="canDropToRoot"
          :showContext="el.expand"
          :disabled="disabled"
          @treeUpdate="onUpdate"
          :unallocDragConfirmCallback="unallocDragConfirmCallback"
        />
      </div>
    </template>
  </draggable>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator'
import draggable, { MoveEvent, DropContext, DraggedContext } from 'vuedraggable'

import { TreeData } from '@/models'
import { OfficeAllocType } from '@/commons/constants'

/**
 * ドラッグツリーコンポーネント
 *
 * @class
 */
@Component({
  name: 'officeAllocDraggableTree',
  components: {
    draggable
  }
})
export default class OfficeAllocDraggableTree extends Vue {
  @Prop()
  data!: TreeData[]

  @Prop()
  type: string | undefined

  @Prop({
    default: 'treeMaster'
  })
  group: string | undefined

  @Prop({
    default: true
  })
  showContext: boolean | undefined

  @Prop({
    default: false
  })
  canDropToRoot: boolean | undefined

  @Prop({
    default: false
  })
  disabled: boolean | undefined

  @Prop({
    default: false
  })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  unallocDragConfirmCallback!: any

  dragOptions = {
    animation: 200,
    ghostClass: 'ghost'
  }

  @Watch('showContext', { immediate: true })
  isContextShow(): boolean {
    return this.showContext as boolean
  }

  @Emit('treeUpdate')
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  treeUpdate(event: Record<string, DropContext<TreeData>>): any {
    return {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      event: event,
      instance: this
    }
  }

  isLast(el: TreeData): boolean {
    return el.isLastNode || false
  }

  onExpandIconClick(el: TreeData): void {
    this.$log.debug('DraggableTree > onExpandIconClick, el:', el)
    el.expand = !el.expand
    this.$forceUpdate()
  }

  // eslint-disable-next-line max-lines-per-function
  checkMove(evt: MoveEvent<TreeData>): boolean {
    this.$log.debug('DraggableTree > checkMove, evt:', evt)
    if (this.disabled) {
      return false
    }

    const { draggedContext, related, to, relatedContext } = evt
    if (draggedContext.element.disabled) {
      return false
    }

    if (!this.canDropToRoot && related.classList.contains('root-node')) {
      return false
    }

    if (to && to.parentElement && to.parentElement.classList.contains('last-node')) {
      return false
    }

    if (!this.canNodeTragToUnallocTree(draggedContext, relatedContext)) {
      if (this.unallocDragConfirmCallback) {
        this.unallocDragConfirmCallback()
      }
    }

    return true
  }

  /**
   * 営業所階層一覧から未割当営業所一覧へ、ドラッグした営業所の管理対象有無がtrueの場合、ドラッグ不可。
   *
   * @param {DraggedContext<TreeData>} draggedContext ドラッグ元のコンテキスト
   * @param {DropContext<TreeData>} relatedContext ドラッグ先のコンテキスト
   * @returns {boolean} ドラッグ不可の場合true
   */
  canNodeTragToUnallocTree(draggedContext: DraggedContext<TreeData>, relatedContext: DropContext<TreeData>): boolean {
    if (!relatedContext || !relatedContext.component || !relatedContext.component.$parent) {
      return true
    }

    const { type } = relatedContext.component.$parent.$props
    if (type !== OfficeAllocType.UN_ALLOC) {
      return true
    }

    if (draggedContext.element.canDragToUnalloc) {
      return true
    }

    return false
  }

  onUpdate(event: Record<string, DropContext<TreeData>>): void {
    if (event.added) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const { element, newIndex } = event.added

      // 未割当=>割当済み
      if (this.type === OfficeAllocType.BUSINESS_OFFICE && element.type === OfficeAllocType.UN_ALLOC) {
        element.type = OfficeAllocType.BUSINESS_OFFICE
        element.isLastNode = false
        element.expand = true
        // 割当済み=>未割当
      } else if (this.type === OfficeAllocType.UN_ALLOC && element.type === OfficeAllocType.BUSINESS_OFFICE) {
        element.type = OfficeAllocType.UN_ALLOC
        element.isLastNode = true
        // 子項目ある営業所をドラッグした場合
        if (element.children && element.children.length > 0) {
          this.changeTreeToList(element, {
            index: newIndex + 1
          })
        }
      }
    }

    this.treeUpdate(event)
    this.$forceUpdate()
  }

  changeTreeToList(element: TreeData, indexObj: Record<string, number>): void {
    // 子項目ある営業所をドラッグした場合
    if (element.children && element.children.length > 0) {
      element.children.forEach((el: TreeData) => {
        el.type = OfficeAllocType.UN_ALLOC
        el.isLastNode = true
        this.data.splice(indexObj.index++, 0, el)

        if (element.children && element.children.length > 0) {
          this.changeTreeToList(el, indexObj)
        }
      })

      element.children = []
    }
  }
}
</script>

<style lang="scss" scoped>
.expander {
  margin-right: 4px;
  cursor: pointer;
}

.expandSkeleton {
  width: 28px;
  display: inline-block;
}

.ghost {
  .node {
    background: $C42 !important;
    color: $C01 !important;
  }
}
</style>
